import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../Common/Loading';
import alert_A_bg from '../../Common/Images/ImagesForAlert/alert_A_bg.png';
import alert_bg from '../../Common/Images/ImagesForAlert/alert_Q_bg_small.png';
import ok_n from '../../Common/Images/ImagesForAlert/ok_n.png';
import ok_o from '../../Common/Images/ImagesForAlert/ok_o.png';
import yes_n from '../../Common/Images/ImagesForAlert/yes_n.png';
import yes_o from '../../Common/Images/ImagesForAlert/yes_o.png';
import no_n from '../../Common/Images/ImagesForAlert/no_n.png';
import no_o from '../../Common/Images/ImagesForAlert/no_o.png';
import EL_PackagesARD from './EL_PackagesARD';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Package {
    PackageCode: string;
    SchoolCode: string;
    PackageDescription: string;
    ProductNames: string[];
    Currency: string;
    ListPrice: number;
    Price_W: number;
    Price_1: number;
    Price_3: number;
    Price_6: number;
    Price_12: number;
    Curriculum: string;
    Subjects: string;
    YearStart: string;
    YearEnd: string;
    NoOfMDs: number;
    Status: string;
    TrialPeriod: string;
    PackageContains: string;
}

const EL_ManagePackages: React.FC = () => {
    // Data States
    const navigate = useNavigate();
    const [packages, setPackages] = useState<Package[]>([]);
    const [schools, setSchools] = useState<string[]>(['All']);
    const [loading, setLoading] = useState<boolean>(false);

    const [filterSchool, setFilterSchool] = useState<string>('All');
    const [filterPkgCode, setFilterPkgCode] = useState<string>('');
    const [filterProdID, setFilterProdID] = useState<string>('');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [selectedPkgCode, setSelectedPkgCode] = useState<string | null>(null);

    const [alertMsg, setAlertMsg] = useState('');
    const [confirmConfig, setConfirmConfig] = useState<{ msg: string, onConfirm: () => void } | null>(null);
    const [showARD, setShowARD] = useState(false);
    const [isAddAction, setIsAddAction] = useState(false);

    const handleShowWarning = (msg: string) => setAlertMsg(msg);

    const handleShowConfirm = (msg: string, onConfirm: () => void) => {
        setConfirmConfig({ msg, onConfirm });
    };

    const handleConfirmYes = () => {
        if (confirmConfig) confirmConfig.onConfirm();
        setConfirmConfig(null);
    };

    useEffect(() => {
        const previousColor = document.body.style.backgroundColor;
        document.body.style.backgroundColor = 'white';
        fetchSchools();
        fetchPackages(1);
        return () => {
            document.body.style.backgroundColor = previousColor;
        };
    }, []);

    const fetchSchools = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}api/manage-packages/schools/`);
            if (response.ok) {
                const data = await response.json();
                setSchools(['All', ...data]);
            }
        } catch (error) {
            console.error("Error fetching schools:", error);
        }
    };

    const fetchPackages = async (page: number) => {
        setLoading(true);
        setSelectedPkgCode(null);
        setCurrentPage(page);

        const params = new URLSearchParams();
        params.append('school_code', filterSchool);
        params.append('package_code', filterPkgCode);
        params.append('prod_id', filterProdID);
        params.append('page', page.toString());
        params.append('page_size', pageSize.toString());

        try {
            const response = await fetch(`${API_BASE_URL}api/manage-packages/?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                if (data.results) {
                    setPackages(data.results);
                    setTotalCount(data.count);
                } else {
                    setPackages(data);
                    setTotalCount(data.length);
                }
            } else {
                handleShowWarning("Failed to load packages.");
            }
        } catch (error) {
            console.error("Error fetching packages:", error);
            handleShowWarning("Network error.");
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize) || 1;

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            fetchPackages(newPage);
        }
    };

    const handleRowClick = (pkg: Package) => {
        if (selectedPkgCode !== pkg.PackageCode) {
            setSelectedPkgCode(pkg.PackageCode);
        }
    };

    const handleAdd = () => {
        setIsAddAction(true);
        setShowARD(true);
    };

    const handleModify = () => {
        if (selectedPkgCode === null) {
            handleShowWarning("Please select a Package to Modify");
            return;
        }
        setIsAddAction(false);
        setShowARD(true);
    };

    const styles = {
        container: {
            fontFamily: 'Arial',
            fontSize: '10pt',
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
            backgroundColor: 'white'
        },
        headerStrip: {
            backgroundColor: '#ECFACB',
            color: 'Maroon',
            fontSize: '14pt',
            fontWeight: 'bold' as const,
            textAlign: 'center' as const,
            padding: '4px',
            flexShrink: 0
        },
        managePackagesHeader: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            textAlign: 'center' as const,
            fontWeight: 'bold' as const,
            padding: '8px',
            fontSize: '10pt',
            borderLeft: '1px solid #7F9DB9',
            borderRight: '1px solid #80A0FF',
        },
        filterBar: {
            padding: '10px',
            borderLeft: '1px solid gray',
            borderRight: '1px solid gray',
            borderBottom: '1px solid gray',
            display: 'flex',
            alignItems: 'center',
            fontSize: '12pt',
            fontFamily: 'Arial',
            backgroundColor: 'white',
            flexShrink: 0
        },
        label: { marginRight: '5px' },
        input: {
            border: '1px solid #003300',
            width: '120px',
            marginRight: '20px',
            padding: '1px'
        },
        btn: {
            width: '95px',
            cursor: 'pointer',
            backgroundColor: '#E0E0E0',
            border: '1px solid #80A0FF'
        },
        gridWrapper: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'hidden',
            border: '1px solid #80A0FF',
        },
        gridContainer: {
            flex: 1,
            overflow: 'auto',
            backgroundColor: 'white'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontSize: '10pt',
            border: "1px solid #80A0FF"
        },
        th: {
            backgroundColor: '#C7DDFD',
            color: '#2859ac',
            borderRight: '1px solid #80A0FF',
            borderBottom: '1px solid #80A0FF',
            padding: '8px',
            position: 'sticky' as const,
            top: 0,
            zIndex: 1,
            textAlign: 'center' as const,
            whiteSpace: 'nowrap' as const,
            fontWeight: 'normal' as const
        },
        td: {
            borderRight: '1px solid #80A0FF',
            padding: '8px',
            verticalAlign: 'top' as const,
            whiteSpace: 'nowrap' as const
        },
        paginationBar: {
            backgroundColor: '#C7DDFD',
            borderTop: '2px solid #80A0FF',
            padding: '8px',
            textAlign: 'center' as const,
            fontSize: '10pt',
            fontFamily: 'Arial',
            color: 'blue',
            fontWeight: 'bold' as const,
            flexShrink: 0,
            userSelect: 'none' as const
        },
        navLink: {
            textDecoration: 'underline',
            cursor: 'pointer',
            margin: '0 4px',
        },
        disabledLink: {
            margin: '0 4px',
        },
        bottomActions: {
            padding: '15px',
            textAlign: 'center' as const,
            flexShrink: 0
        },
        actionBtn: {
            width: '90px',
            cursor: 'pointer',
            marginRight: '5px'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerStrip}>List of Packages</div>
            <div style={styles.filterBar}>
                <span style={styles.label}>SchoolCode:</span>
                <select
                    value={filterSchool}
                    onChange={(e) => setFilterSchool(e.target.value)}
                    style={{ ...styles.input, width: '208px' }}
                >
                    {schools.map(s => <option key={s} value={s}>{s}</option>)}
                </select>

                <span style={styles.label}>Package Code:</span>
                <input
                    type="text"
                    maxLength={10}
                    value={filterPkgCode}
                    onChange={(e) => {
                        const onlyNums = e.target.value.replace(/[^0-9]/g, '');
                        setFilterPkgCode(onlyNums);
                    }}
                    style={styles.input}
                />

                <span style={styles.label}>Containing Product Code:</span>
                <input
                    type="text"
                    maxLength={10}
                    value={filterProdID}
                    onChange={(e) => setFilterProdID(e.target.value)}
                    style={styles.input}
                />

                <button style={styles.btn} onClick={() => fetchPackages(1)}>SELECT</button>
            </div>

            <div style={styles.managePackagesHeader}>Manage Packages</div>

            <div style={styles.gridWrapper}>
                <div style={styles.gridContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th colSpan={6} style={styles.th}></th>
                                <th colSpan={5} style={{ ...styles.th, borderLeft: '1px solid white', padding: '8px' }}>Subscription Price</th>
                                <th colSpan={2} style={styles.th}></th>
                                <th colSpan={2} style={{ ...styles.th, borderLeft: '1px solid white' }}>Grade/Year</th>
                                <th colSpan={4} style={styles.th}></th>
                            </tr>

                            <tr>
                                <th style={{ ...styles.th, textAlign: 'left' }}>PackageCode</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>SchoolCode</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Package Description</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Product(s)</th>
                                <th style={styles.th}>Currency</th>
                                <th style={styles.th}>List Price</th>
                                <th style={styles.th}>Weekly</th>
                                <th style={styles.th}>1 Month</th>
                                <th style={styles.th}>3 Months</th>
                                <th style={styles.th}>6 Months</th>
                                <th style={styles.th}>12 Months</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Curriculum</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Subject(s)</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Start</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>End</th>
                                <th style={styles.th}>MDs</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Package Contains</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Status</th>
                                <th style={{ ...styles.th, textAlign: 'left' }}>Trial<br />Period</th>
                            </tr>
                        </thead>
                        <tbody>

                            {loading ? (
                                <tr><td colSpan={19} align="center"><Loading /></td></tr>
                            ) : packages.map((row, index) => {
                                const isSelected = selectedPkgCode === row.PackageCode;
                                const bg = isSelected ? 'orange' : (index % 2 === 0 ? '#F4FAFF' : 'white');
                                const color = isSelected ? 'white' : 'black';

                                return (
                                    <tr
                                        key={row.PackageCode}
                                        onClick={() => handleRowClick(row)}
                                        style={{ backgroundColor: bg, cursor: 'pointer', color: color }}
                                    >
                                        <td style={styles.td}>{row.PackageCode}</td>
                                        <td style={styles.td}>{row.SchoolCode}</td>
                                        <td style={styles.td}>{row.PackageDescription}</td>
                                        <td style={styles.td}>
                                            {row.ProductNames.map((p, i) => (
                                                <div key={i}>{p}</div>
                                            ))}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Currency}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.ListPrice}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Price_W}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Price_1}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Price_3}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Price_6}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.Price_12}</td>
                                        <td style={styles.td}>{row.Curriculum}</td>
                                        <td style={styles.td}>{row.Subjects}</td>
                                        <td style={styles.td}>{row.YearStart}</td>
                                        <td style={styles.td}>{row.YearEnd}</td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>{row.NoOfMDs}</td>
                                        <td style={styles.td}>{row.PackageContains}</td>
                                        <td style={styles.td}>{row.Status}</td>
                                        <td style={styles.td}>{row.TrialPeriod}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div style={styles.paginationBar}>
                    <span
                        style={currentPage > 1 ? styles.navLink : styles.disabledLink}
                        onClick={() => currentPage > 1 && handlePageChange(1)}
                    >
                        &lt;&lt;
                    </span>
                    <span
                        style={currentPage > 1 ? styles.navLink : styles.disabledLink}
                        onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                    >
                        &lt;
                    </span>

                    <span style={{ margin: '0 10px', color: 'blue', textDecoration: 'none' }}>
                        {currentPage} of {totalPages}
                    </span>

                    <span
                        style={currentPage < totalPages ? styles.navLink : styles.disabledLink}
                        onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                    >
                        &gt;
                    </span>
                    <span
                        style={currentPage < totalPages ? styles.navLink : styles.disabledLink}
                        onClick={() => currentPage < totalPages && handlePageChange(totalPages)}
                    >
                        &gt;&gt;
                    </span>
                </div>
            </div>

            <div style={styles.bottomActions}>
                <button style={styles.actionBtn} onClick={handleAdd}>Add</button>
                {/* <button style={styles.actionBtn} disabled={!selectedPkgCode} onClick={handleModify}>Modify</button> */}
                <button
                    style={styles.actionBtn}
                    disabled={selectedPkgCode === null}
                    onClick={handleModify}
                >
                    Modify
                </button>
                <button style={styles.actionBtn} onClick={() => navigate('/SysAdmin')}>Back</button>
            </div>

            {alertMsg && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '400px', height: '200px', backgroundImage: `url(${alert_A_bg})`, backgroundRepeat: 'no-repeat', backgroundSize: '100% 100%', position: 'relative', fontFamily: 'Tahoma, Arial, sans-serif', fontSize: '12px', color: 'black' }}>
                        <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>Warning</div>
                        <div style={{ position: 'absolute', top: '40px', left: '100px', right: '15px', bottom: '50px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <span style={{ fontWeight: 'normal', marginTop: '-25px', fontSize: '11pt' }}>{alertMsg}</span>
                        </div>
                        <div style={{ position: 'absolute', bottom: '10px', width: '100%', textAlign: 'center' }}>
                            <img src={ok_n} alt="OK" onClick={() => setAlertMsg('')} style={{ cursor: 'pointer' }} onMouseOver={(e) => e.currentTarget.src = ok_o} onMouseOut={(e) => e.currentTarget.src = ok_n} />
                        </div>
                    </div>
                </div>
            )}

            {confirmConfig && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '306px', height: '210px', backgroundImage: `url(${alert_bg})`, backgroundRepeat: 'no-repeat', position: 'relative', fontFamily: 'Tahoma', fontSize: '13px' }}>
                        <div style={{ position: 'absolute', top: '5px', left: '0', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' }}>Confirmation</div>
                        <div style={{ position: 'absolute', top: '35px', left: '70px', right: '10px', height: '110px', display: 'flex', alignItems: 'center', color: '#525252', fontSize: '11pt' }}>
                            {confirmConfig?.msg}
                        </div>
                        <div style={{ position: 'absolute', bottom: '15px', width: '100%', textAlign: 'center' }}>
                            <img src={yes_n} alt="Yes" onClick={handleConfirmYes} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = yes_o} onMouseOut={(e) => e.currentTarget.src = yes_n} />
                            <img src={no_n} alt="No" onClick={() => setConfirmConfig(null)} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = no_o} onMouseOut={(e) => e.currentTarget.src = no_n} />
                        </div>
                    </div>
                </div>
            )}

            {showARD && (
                <EL_PackagesARD
                    pkgCode={isAddAction ? null : selectedPkgCode}
                    onClose={() => setShowARD(false)}
                    onSaveSuccess={() => {
                        fetchPackages(currentPage);
                        setShowARD(false);
                    }}
                    showWarning={handleShowWarning}
                    showConfirm={handleShowConfirm}
                />
            )}
        </div>
    );
}

export default EL_ManagePackages;